import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../store';
import { useChatMessages } from './hooks/useChatMessages';
import { ChatHeader } from './components/chat-header';
import { MessageList } from './components/message-list';
import { ChatFooter } from './components/chat-footer';
import { ChatToggle } from './components/chat-toggle';
import './chat-bot.scss';

export const ChatBot = observer(() => {
    const {customerStore} = useStores();

    const [isOpen, setIsOpen] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    // drag state
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragOffsetRef = useRef<{ offsetX: number; offsetY: number } | null>(null);
    const chatWindowRef = useRef<HTMLDivElement | null>(null);
    const chatHeaderRef = useRef<HTMLDivElement | null>(null);

    const {
        messages,
        displayActionButtons,
        isAgentLoading,
        lastUserMessage,
        onActionButtonClick,
        setLastUserMessage,
        sendMessage,
        isInitial
    } = useChatMessages(customerStore.customerId, customerStore.serviceId, isOpen);

    const toggleChat = () => setIsOpen(!isOpen);
    const toggleMaximize = () => setIsMaximized(!isMaximized);

    const handleChatMouseDown = (e: MouseEvent) => {
        if (!isOpen) return;
        if ((e.target as HTMLElement).closest('.chat-header-actions') ||
            (e.target as HTMLElement).closest('.close-button')) return; // ignore action buttons
        if (!chatWindowRef.current) return;
        const rect = chatWindowRef.current.getBoundingClientRect();
        dragOffsetRef.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
        setIsDragging(true);
        e.preventDefault();
    };

    // Native listener: dragging is a pointer-only enhancement, not a keyboard-operable control.
    useEffect(() => {
        const header = chatHeaderRef.current;
        header?.addEventListener('mousedown', handleChatMouseDown);
        return () => header?.removeEventListener('mousedown', handleChatMouseDown);
    }, [isOpen]);

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!chatWindowRef.current || !dragOffsetRef.current) return;
            const rect = chatWindowRef.current.getBoundingClientRect();
            let newX = e.clientX - dragOffsetRef.current.offsetX;
            let newY = e.clientY - dragOffsetRef.current.offsetY;
            const maxX = window.innerWidth - rect.width;
            const maxY = window.innerHeight - rect.height;
            newX = Math.min(Math.max(0, newX), maxX);
            newY = Math.min(Math.max(0, newY), maxY);
            setDragPos({ x: newX, y: newY });
        };

        const handleMouseUp = () => setIsDragging(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    useEffect(() => {
        if (!isOpen) {
            setDragPos(null);
            setIsDragging(false);
            dragOffsetRef.current = null;
        }
    }, [isOpen]);

    return (
        <div>
            <div ref={chatWindowRef}
                 className={`chat-window ${isOpen ? 'open' : ''} ${isMaximized ? 'max-width' : ''} ${isInitial ? 'initial' : ''} ${dragPos ? 'dragged' : ''}`}
                 style={dragPos ? { top: dragPos.y, left: dragPos.x } : undefined}
            >
                <div className="chat-header" ref={chatHeaderRef}>
                    <ChatHeader
                        isOpen={isOpen}
                        isInitial={isInitial}
                        isMaximized={isMaximized}
                        toggleMaximize={toggleMaximize}
                        toggleChat={toggleChat}
                    />
                </div>
                <div className="chat-body">
                    <MessageList
                        messages={messages}
                        isAgentLoading={isAgentLoading}
                        isInitial={isInitial}
                        showActionButtons={displayActionButtons}
                        onActionButtonClick={onActionButtonClick}
                    />
                </div>
                <ChatFooter
                    value={lastUserMessage}
                    setValue={setLastUserMessage}
                    sendMessage={sendMessage}
                />
            </div>
            {!isOpen && <ChatToggle onClick={toggleChat}/>}
        </div>
    );
});
